interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: BeforeInstallPromptEvent | undefined;
const listeners = new Set<() => void>();

function announce(): void {
  for (const listener of listeners) listener();
}

export function installPromptAvailable(): boolean {
  return deferred !== undefined;
}

export function subscribeInstallPrompt(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function runningAsInstalledApp(): boolean {
  return window.matchMedia("(display-mode: standalone)").matches;
}

// Must be called from a click handler — the browser discards the stashed prompt otherwise.
export async function showInstallPrompt(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const event = deferred;
  if (!event) return "unavailable";
  await event.prompt();
  const { outcome } = await event.userChoice;
  deferred = undefined;
  announce();
  return outcome;
}

// Captured at module scope because beforeinstallprompt can fire before React mounts, and the event
// is only usable if preventDefault() ran synchronously.
export function captureInstallPrompt(): void {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    announce();
  });
  window.addEventListener("appinstalled", () => {
    deferred = undefined;
    announce();
  });
}
